import Model from '@ember-data/model';
import { Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { ICON_SORTS } from "../../constants/lsg";
import { ERROR_MESSAGES } from "../../constants/error-messages";

/**
 * The possible sorts for the call to loadApplications.
 *
 * TODO - this really should be updated with the list from the 'postings' stateManager.
 *
 * @type {Object}
 */
export const LOAD_APPLICATIONS_SORTS = {
  DATE_ACTIVE: 'date-active',
  DATE_STARTED: 'date-started',
  LAST_ACTIVITY: 'last-activity',
};

/**
 * The defaults for the request to loadApplications
 *
 * @type {Object}
 */
export const LOAD_APPLICATIONS_DEFAULTS = {
  APPLICATIONS_PER_PAGE: 10,
  DEFAULT_SORT: LOAD_APPLICATIONS_SORTS.LAST_ACTIVITY,
  DEFAULT_SECONDARY_SORT: ICON_SORTS.SORT_DESCENDING
};

/** @type {Object} The includes for fetching test billing record for a buyer or seller. */
export const TEST_BILLING_RECORD_INCLUDES = {
  buyer: [
    'expedite-requestor',
    'fee-structure',
    'sh-invoice-line-items.derived-from-project-line-item',
    'sh-invoice-line-items.derived-from-sh-invoice-line-item',
    'sh-invoice-line-items.related-sh-invoice-line-item',
  ],
  seller: [
    'expedite-requestor',
    'fee-structure',
    'sh-payment-line-items.derived-from-project-line-item',
    'sh-payment-line-items.derived-from-sh-payment-line-item',
    'sh-payment-line-items.related-sh-payment-line-item',
  ],
};

/**
 * Service for application related API calls.
 */
export default Service.extend({
  /**
   * Services
   */
  api: service(),
  store: service(),
  threadManager: service('managers/thread-manager'),
  errorTracker: service('error-tracker'),
  session: service(),
  shData: service(),
  appCache: service(),

  /**
   * @param {DS.Model|string} assignment
   * @param {object} form
   * @param {string[]|undefined} opt_includes
   */
  createApplication(assignment, form, opt_includes = []) {
    const id = (assignment instanceof Model) ? assignment.get('id') : assignment;

    const options = {
      jsonData: {
        ...form,
      },
    };

    this.shData.addIncludesToOptions(options, opt_includes);

    return this.get('api').post(`postings/${id}/applications`, options).then((response) => {
      const store = this.get('store');
      store.pushPayload(response);
      return store.peekRecord('application', response.data.id);
    });
  },

  /**
   * Attaches an asset to an application.
   *
   * @param {Application} application
   * @param {Asset} asset
   * @returns {Promise}
   */
  attachAssetToApplication(application, asset) {
    const assetID = asset.data.id;
    const endpoint = `/applications/${application.get('id')}/assets/${assetID}/attach`;
    const options = {};

    return this.get('api').post(endpoint, options)
      .then(() => {
        this.shData.pushResponseToStore(asset);
        const assetModel = this.get('store').peekRecord('asset', assetID);
        application.get('assets').addObject(assetModel);
      });
  },

  /**
   * Add a favorite status from an application.
   *
   * @param {Application} application
   */
  addFavorite(application) {
    application.set('isFavorite', true);

    return new Promise((resolve, reject) => {
      application.addFavorite().then(() => {
        resolve(application);
      }).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error adding user to your network. Please refresh and try again.',
          'addFavorite -- error adding favorite. app id: ' + application.get('id'),
        );

        application.set('isFavorite', false);
        reject(e);
      });
    });
  },

  /**
   * Add followers for the given application id
   *
   * @param {String} applicationId
   * @param {Array<String>} followerIds
   * @param {Array<String>} opt_includes
   */
  addFollowers(applicationId, followerIds, opt_includes = []) {
    let options = {
      jsonData: {
        'user-ids' : followerIds
      }
    };

    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').post(`applications/${applicationId}/addFollowers`, options).then(() => {
        let application = this.get('store').peekRecord('application', applicationId);
        if (!application) {
          resolve(true);
          return;
        }

        let followers = application.get('followers');
        followerIds.forEach(followerId => {
          let userFromCache = this.get('store').peekRecord('user', followerId);
          if (userFromCache) {
            followers.addObject(userFromCache);
          }
        });

        resolve(true);
      }).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error saving your followers. Please refresh and try again.',
          'addFollowers -- error saving followers. app id: ' + applicationId,
        );
        reject(e);
      });
    });
  },

  /**
   * Add a note on the application from the buyer on the seller
   *
   * @param {Application} application
   * @param {String} note
   * @return {Ember.RSVP.Promise}
   */
  addFreelancerNoteFromBuyer(application, note) {
    let options = {
      jsonData: {
        'note' : note
      }
    };

    this.shData.addIncludesToOptions(options, []);

    return new Promise((resolve, reject) => {
      this.get('api').post(`applications/${application.get('id')}/buyer-freelancer-note`, options).then(() => {
        resolve(true);
      }).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error adding your note. Please refresh and try again.',
          'addFreelancerNoteFromBuyer -- error saving followers. app id: ' + application.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Add a private comment on this application from the buyer.
   *
   * @param {Application} application
   * @param {String} note
   * @return {Ember.RSVP.Promise}
   */
  addPrivateCommentFromBuyer(application, note) {
    let options = {
      jsonData: {
        'comment' : note
      }
    };

    this.shData.addIncludesToOptions(options, []);

    return new Promise((resolve, reject) => {
      this.get('api').post(`applications/${application.get('id')}/buyer-private-comment`, options).then(() => {
        resolve(true);
      }).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error adding your private comment. Please refresh and try again.',
          'addPrivateCommentFromBuyer -- error sending private comment. app id: ' + application.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Add a seller review on the application about the buyer.
   *
   * @param {Application} application
   * @param {Number} score
   * @param {String} opt_comment
   * @return {Ember.RSVP.Promise}
   */
  addSellerReview(application, score, opt_comment = '') {
    let options = {
      jsonData: {
        'score' : score
      }
    };

    if (opt_comment) {
      options.jsonData['comment'] = opt_comment;
    }

    this.shData.addIncludesToOptions(options, []);

    return new Promise((resolve, reject) => {
      this.get('api').post(`applications/${application.get('id')}/seller-review`, options).then(() => {
        resolve(true);
      }).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error creating your review. Please refresh and try again.',
          'addSellerReview -- error setting seller review. app id: ' + application.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Agree to a legal for an application.
   *
   * @param {Application} application
   * @return {Ember.RSVP.Promise}
   */
  agreeToLegal(application) {
    return this.api.post(`applications/${application.id}/accept-legal-agreements`, {});
  },

  /**
   * Add a review FROM a buyer
   *
   * @param {Application} application
   * @param {Number} starCount
   * @param {String} opt_message
   * @return {Ember.RSVP.Promise}
   */
  addReviewBuyer(application, starCount, opt_message = '') {
    return new Promise((resolve, reject) => {
      let options = {
        jsonData: {
          'score' : starCount
        }
      };

      if (opt_message) {
        options.jsonData['comment'] = opt_message;
      }

      this.shData.addIncludesToOptions(options, []);

      this.get('api').post(`applications/${application.get('id')}/buyer-review/`, options).then(() => {
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error adding your review. Please refresh and try again.',
          'addReviewBuyer -- error adding review: ' + application.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Update a review FROM an admin
   *
   * @param {Application} application
   * @param {Number} starCount
   * @param {String} opt_message
   * @return {Ember.RSVP.Promise}
   */
  editReviewAdmin(application, starCount, opt_message = '') {

    const options = {
      jsonData: {
        'score' : starCount,
        'comment' : opt_message
      }
    }

    this.shData.addIncludesToOptions(options, []);

    return this.api.put(`applications/${application.id}/buyer-review/`, options).catch(e => {
      this.errorTracker.error('editReviewAdmin failed - ' + e.toString(), this);
      throw e;
    });
  },

  /**
   * Called to approve the offer of an application
   *
   * @param {String} applicationId
   * @param {Array<String>=} opt_applicationIncludes    Optional includes for the application fetch after the offer is made.
   */
  approveOffer(applicationId) {
    return this.api.post(`applications/${applicationId}/accept-offer`, {});
  },

  /**
   * Atach an asset from the company library
   *
   * @param {Application} application
   * @param {Asset} asset
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  attachAssetFromLibrary(application, asset, opt_includes = []) {
    return new Promise((resolve, reject) => {
      let options = {
        jsonData: {}
      };

      this.shData.addIncludesToOptions(options, opt_includes);

      this.get('api').post(`applications/${application.get('id')}/assets/${asset.get('id')}/attach`, options).then(response => {
        application.get('assets').addObject(asset);
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error uploading your assets. Please refresh and try again.',
          'attachAssetFromLibrary -- error attaching asset - asset id: ' + asset.get('id') + ' appid: ' + application.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Cancel an application
   *
   * @param {Application} application
   * @param {Number=} opt_cancelFinalPayment
   * @param {String=} opt_reason        of type PROJECT_CANCELLATION__CANCELLATION_REASONS.*
   * @param {String=} opt_comment
   * @param {Boolean} opt_isConfirm     true if this is a cancellation confirm.
   * @return {Ember.RSVP.Promise}
   */
  cancelApplication(
    application,
    opt_cancelFinalPayment = null,
    opt_reason = '',
    opt_comment = '',
    opt_isConfirm = false,
    userStaleCancel = false
  ) {
    const options = {
      jsonData: {
        userStaleCancel,
      }
    };

    if (!opt_isConfirm) {
      options.jsonData['cancel-final-payment'] = opt_cancelFinalPayment;
    }

    if (opt_reason) {
      options.jsonData['reason'] = opt_reason;
    }

    if (opt_comment) {
      options.jsonData['comment'] = opt_comment;
    }

    return this.api.post(`applications/${application.id}/cancel`, options);
  },

  /**
   * Change the application legal selected to the new one passed in.
   *
   * @param {Application} application
   * @param {Legal} legal
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  changeApplicationLegal(application, legal, opt_includes = []) {
    let options = {
      jsonData: {
        'legal_id' : legal.get('id')
      }
    };

    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').post(`applications/${application.get('id')}/change-legal`, options).then(() => {
        let currentLegals = application.get('legals');
        if (currentLegals) {
          currentLegals.clear();
          currentLegals.pushObject(legal);
        }

        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error changing your contract. Please refresh and try again.',
          'changeApplicationLegal -- error changing legal for appid: ' + application.get('id') + ' and legal id ' + legal.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Decline an application with an optional message
   *
   * @param {Application} application
   * @param {String=} opt_message
   * @return {Ember.RSVP.Promise}
   */
  declineApplication(application, opt_message) {
    let options = {};
    if (opt_message) {
      options = {
        jsonData: {
          'message' : opt_message
        }
      };
    }

    this.shData.addIncludesToOptions(options, []);

    return new Promise((resolve, reject) => {
      this.api.post(`/applications/${application.id}/decline`, options).then(() => {
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error declining the application. Please refresh and try again.',
          'declineApplication -- NOT MASS -- error declining app id: ' + application.get('id'),
        );

        reject(e);
      });
    });
  },

  /**
   * Undecline an application
   *
   * @param {String} applicationId
   * @return {Ember.RSVP.Promise}
   */
  undeclineApplication(applicationId) {
    return this.api.post(`/applications/${applicationId}/undecline`, {});
  },

  /**
   * Given an array of application ids, decline those with an optional message.
   *
   * @param {Array<String>} applicationIds
   * @param {String=} opt_message
   * @param {Array<String>=} opt_includes
   */
  declineApplications(applicationIds, opt_message, opt_includes = []) {
    let options = {
      jsonData: {
        'application_ids' : applicationIds
      }
    };

    if (opt_message) {
      options.jsonData['message'] = opt_message;
    }

    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').post(`/applications/mass-decline`, options).then(() => {
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error declining the application(s). Please refresh and try again.',
          'declineApplications -- error declining app ids: ' + applicationIds.join(','),
        );

        reject(e);
      });
    });
  },

  /**
   * Expedite the payout and update our state.
   *
   * @param {Application} application
   * @param {Array<String>=} opt_applicationIncludes
   * @return {Ember.RSVP.Promise}
   */
  expeditePayout(application, opt_applicationIncludes = []) {
    const options = this.shData.addIncludesToOptions({}, opt_applicationIncludes);

    return this.get('api').post(`applications/${application.get('id')}/mark-expedited/`, options).then(response => {
      return this._pushApplicationResponseToStore(response);
    }).catch(e => {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error expediting your payout. Please refresh and try again.',
        'expeditePayout -- error expediting payout app id: ' + application.get('id'),
      );

      throw e;
    });
  },

  /**
   * Take over fees for expedited payout
   *
   * @param {Application} application
   * @param {Array<String>=} opt_applicationIncludes
   * @return {Ember.RSVP.Promise}
   */
  expeditePayoutTakeoverFees(application, opt_applicationIncludes = []) {
    const options = this.shData.addIncludesToOptions({
      jsonData: {
        takeover_expedite_fee: true,
      },
    }, opt_applicationIncludes);

    return this.api.post(`applications/${application.id}/mark-expedited`, options).then(response => {
      return this._pushApplicationResponseToStore(response);
    }).catch(e => {
      this.errorTracker.error(`There was an error taking over expedite fee for application [${application.id}]`, this, e);

      throw e;
    });
  },

  /**
   * Retrieves the test billing record (invoice/payment) from the server.
   *
   * @param {Object} params
   * @param {Application} params.application The application.
   * @param {String} params.userType The type of user.
   * @param {Number} params.cancellationAmount The user input value.
   * @param {Boolean} params.expedite Whether the payment is expedited.
   * @param {Boolean} params.takeoverExpediteFee Whether the current user is taking over the expedite fees
   * @param {Number} params.price The offer price for the project.
   * @param {Boolean} params.isAdvance Is this an Advance, or standard project completion
   * @param {Number} params.advanceBaseAmount
   * @param {Ember.A<Number>} params.advanceProjectLineItemIds
   * @param {String} params.advanceBaseDescription
   * @param {Boolean} params.recalculateFees Whether to recalculate fees when fetching record.
   * @returns {Promise} The test billing record.
   */
  getTestBillingRecord({ application,
                         userType,
                         cancellationAmount,
                         expedite,
                         takeoverExpediteFee,
                         price,
                         isAdvance,
                         advanceBaseAmount,
                         advanceProjectLineItemIds,
                         advanceBaseDescription,
                         recalculateFees,
  }) {
    const applicationID = application.get('id');
    const includes = TEST_BILLING_RECORD_INCLUDES[userType];

    const billableType = userType === 'seller' ? 'sh-payment' : 'sh-invoice';
    const requestPart = isAdvance ? `test-advance-${billableType}` : `test-${billableType}`;

    const endpoint = `applications/${applicationID}/${requestPart}/`;

    let options = {
      jsonData: {},
    };

    if (cancellationAmount) {
      options.jsonData['cancel-final-payment'] = cancellationAmount;
    }

    if (expedite != null) {
      options.jsonData['expedite'] = expedite;

      if (takeoverExpediteFee != null) {
        options.jsonData['takeover-expedite-fee'] = takeoverExpediteFee;
      }
    }

    if (price != null) {
      options.jsonData['price'] = price;
    }

    if (recalculateFees != null) {
      options.jsonData['recalculate_fees'] = recalculateFees;
    }

    if (advanceBaseAmount != null) {
      options.jsonData['base-amount'] = advanceBaseAmount;
    }

    if (advanceProjectLineItemIds != null) {
      options.jsonData['project-line-item-ids'] = advanceProjectLineItemIds;
    }

    if (advanceBaseDescription != null) {
      options.jsonData['base-description'] = advanceBaseDescription;
    }

    options = this.shData.addIncludesToOptions(options, includes);

    return this.get('api').post(endpoint, options);
  },

  /**
   * Load the application dashboard list object from the server and parse in all the information
   * into our internal stores.
   *
   * @param {String}  status              of type APPLICATION.SIMPLE_STATUS.*
   * @param {String} sort                 of type PROJECTS_SORT_API_ENUMS.*
   * @param {String} networkFilter        of type PROJECTS_NETWORK_FILTER_API_ENUMS.*
   * @param {Number|null} companyTeamFilter of type CompanyTeam.id
   * @param {String} secondarySort        of type ICON_SORTS.*
   * @param {Number} page                 The current page number we'd like to fetch
   * @param {Number} count                The count of how many items we want per page.
   * @param {String} cacheKey             The cache key to ensure our results stay in sync b/t pages.
   * @param {Array=} optIncludes          Add your includes for the api.
   *
   * ********* NOTE ***********
   * IF YOU CHANGE THE SIGNATURE, UPDATE THE CACHING MECHANISM TO REMOVE cacheKey CORRECTLY
   *
   */
  loadApplicationDashboardListRequest(status, sort, networkFilter, companyTeamFilter, secondarySort, page, count, cacheKey, optIncludes = []) {
    let options = {
      params: {
        format: 'dashboard',
        sort: sort,
        secondary_sort: secondarySort,
        filter: {
          simple_status: status,
          network: networkFilter,
        },
        page: page,
        cache_key: cacheKey,
        count: count
      }
    };

    const includes = optIncludes.join(',');
    if (includes) {
      options.params['include'] = includes;
    }

    if (companyTeamFilter) {
      options.params.filter['company_team'] = companyTeamFilter;
    }

    /**
     * Removing the cache key since this is randomly generated and would defeat the purpose of the cache.
     */
    let callerArguments = Object.assign({}, arguments);
    delete callerArguments[6]; // index of cacheKey

    let dashboardList = this.appCache.getCachedModelArrayForRequest(this, 'loadApplicationDashboardListRequest', callerArguments);
    if (dashboardList) {
      return Promise.resolve(dashboardList);
    }

    return new Promise((resolve, reject) => {
      this.get('api').request('applications', options).then((response) => {
        let dashboardList = this._parseDashboardList(response);

        this.appCache.setCachedModelArrayForRequest(
          this,
          'loadApplicationDashboardListRequest',
          callerArguments,
          dashboardList);

        resolve(dashboardList);
      }).catch(e => {
        this.get('errorTracker').error('loadApplicationDashboardListRequest - ' + arguments.toString(), this);

        reject(e);
      });
    });
  },

  /**
   * @param {String} applicationIdOrSlug
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise|*}
   */
  loadApplication(applicationIdOrSlug, opt_includes = []) {
    const options = this.shData.addIncludesToOptions({}, opt_includes);

    return this.get('api').request(`applications/${applicationIdOrSlug}`, options);
  },

  /**
   * Load a list of applications filter and sorted by the given params.
   *
   * @param {String=} opt_assignmentId          the id of the assignment to fetch apps for.
   * @param {String=} opt_status                of type APPLICATION.SIMPLE_STATUS.*
   * @param {String=} opt_sort                  of type LOAD_APPLICATIONS_DEFAULTS.*
   * @param {Boolean=} opt_isOnlyShowFavorites  true if we want to filter only by favorites
   * @param {String=} opt_secondarySort         of type LOAD_APPLICATIONS_DEFAULTS.*
   * @param {Number=} opt_page                  The current page number we'd like to fetch
   * @param {Number=} opt_count                 The count of how many items we want per page.
   * @param {Boolean=} opt_cacheKey             Set to true to always skip the client side cache.
   * @param {String=} opt_includes              The comma delimmited include string for the api
   * @param {String=} opt_sellerId              The id of the seller to filter by
   * @param {String=} opt_companyId             The id of the company to filter by
   *
   * @return {Ember.RSVP.Promise}
   */
  loadApplications(
  opt_assignmentId,
  opt_status,
  opt_sort,
  opt_isOnlyShowFavorites,
  opt_secondarySort,
  opt_page,
  opt_count,
  opt_cacheKey,
  opt_includes,
  opt_sellerId,
  opt_companyId,
  opt_serviceId,
  opt_allCompany)
  {
    let assignmentId  = opt_assignmentId,
      status          = opt_status,
      page            = opt_page || 1,
      resultsPerPage  = opt_count || LOAD_APPLICATIONS_DEFAULTS.APPLICATIONS_PER_PAGE,
      sort            = opt_sort || LOAD_APPLICATIONS_DEFAULTS.DEFAULT_SORT,
      isOnlyShowfavorites = opt_isOnlyShowFavorites,
      secondarySort   = opt_secondarySort || LOAD_APPLICATIONS_DEFAULTS.DEFAULT_SECONDARY_SORT,
      cacheKey        = opt_cacheKey,
      allCompanies    = opt_allCompany || false;

    let options = {
      params: {
        filter: {}
      }
    };

    if (assignmentId) {options.params.filter['assignment_id'] = assignmentId;}
    if (status) {options.params.filter['simple_status'] = status;}
    if (isOnlyShowfavorites) {options.params.filter['favorite'] = true;}
    if (page) {options.params['page'] = page;}
    if (resultsPerPage) {options.params['count'] = resultsPerPage;}
    if (cacheKey) {options.params['cache_key'] = cacheKey;}
    if (sort) {options.params['sort'] = sort;}
    if (secondarySort) {options.params['secondary_sort'] = secondarySort;}
    if (opt_sellerId) {options.params.filter['seller_id'] = opt_sellerId;}
    if (opt_companyId) {options.params.filter['company_id'] = opt_companyId;}
    if (opt_serviceId) {options.params.filter['serviceId'] = opt_serviceId;}
    if (allCompanies) {options.params['all_company'] = allCompanies;}

    if (opt_includes) {
      options.params.include = opt_includes;
    }

    return new Promise((resolve, reject) => {
      this.get('store').query('application', options.params).then((applicationsResponse) => {
        let assignmentDetailsApplicationList = this.get('store').createRecord('assignmentDetailsApplicationList');

        //add meta info
        let meta = applicationsResponse.get('meta');
        if (meta) {
          assignmentDetailsApplicationList.set('hiredCount', meta.hired_count);
          assignmentDetailsApplicationList.set('pendingCount', meta.pending_count);
          assignmentDetailsApplicationList.set('declinedCount', meta.declined_count);
          assignmentDetailsApplicationList.set('hiredIsRead', meta.hired_is_read);
          assignmentDetailsApplicationList.set('pendingIsRead', meta.pending_is_read);
          assignmentDetailsApplicationList.set('declinedIsRead', meta.declined_is_read);
          assignmentDetailsApplicationList.set('cacheKey', meta.cache_key);
        } else {
          this.get('errorTracker').error('loadApplications no meta - ', this);
        }

        //add pagination
        let paginationMeta = applicationsResponse.get('meta.pagination'),
          pagination = this.get('store').createRecord('pagination');
        if (paginationMeta) {
          pagination.set('total', paginationMeta.total);
          pagination.set('count', paginationMeta.count);
          pagination.set('perPage', paginationMeta.per_page);
          pagination.set('currentPage', paginationMeta.current_page);
          pagination.set('totalPages', paginationMeta.total_pages);
        } else {
          this.get('errorTracker').error('loadApplications no pagination - ', this);
        }

        assignmentDetailsApplicationList.set('pagination', pagination);
        assignmentDetailsApplicationList.set('applications', applicationsResponse);

        resolve(assignmentDetailsApplicationList);
      }).catch(e => {
        this.get('errorTracker').error('loadApplications -- ' + e.toString(), this);
        reject(e);
      });
    });
  },

  /**
   * Make an offer on an application
   *
   * e.g. https://storyhunterlocal.com/applications/counter/54638
   *
   * @param {String} applicationId
   * @param {Number} offerAmount
   * @return {Ember.RSVP.Promise}   Resolved with the app that is updated with the new offer.
   */
  makeOffer(applicationId, price) {
    const options = {
      jsonData: {
        price
      }
    };

    this.shData.addIncludesToOptions(options, []);

    return this.api.post(`applications/${applicationId}/offer`, options);
  },

  /**
   * Mark activity feed items for an application as viewed. If a message ID is supplied, only the
   * messages up to and included that message will be encompassed by the thread's lastReadAt
   * timestamp.
   *
   * @param {Application} application
   * @param {String} messageID
   * @return {Ember.RSVP.Promise}
   */
  markApplicationActivitiesViewed(application, messageID) {
    const applicationID = application.get('id');
    const endpoint = `applications/${applicationID}/mark-viewed/`;
    const includes = ['thread'];

    let options = messageID ? { jsonData: { message_id: messageID } } : {};
    options = this.shData.addIncludesToOptions(options, includes);

    return this.get('api').post(endpoint, options)
      .then((applicationResponse) => {
        const thread = application.get('thread');
        const threadResponse = applicationResponse.included[0];
        const user = this.get('session.currentUser');
        return this.get('threadManager').updateThreadModel(thread, threadResponse, user);
      })
      .catch((error) => {
        const message = `markApplicationActivitiesViewed -- Application ID: ${applicationID}`;
        this.get('errorTracker').error(message, this, error);
        throw error;
      });
  },

  /**
   * Mark a project complete
   *
   * @param {Application} application
   * @return {Ember.RSVP.Promise}
   */
  markComplete(application) {
    return new Promise((resolve, reject) => {
      let options = {};
      this.shData.addIncludesToOptions(options, []);

      this.get('api').post(`applications/${application.get('id')}/complete/`, options).then(() => {
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error marking the project complete. Please refresh and try again.',
          'markComplete -- error marking app complete app id: ' + application.get('id'),
        );
        reject(e);
      });
    });
  },

  markStillActive(application, reason) {
    const options = {
      jsonData: {
        reason
      }
    };

    return this.api.post(`applications/${application.id}/mark-still-active`, options);
  },

  /**
   * Cancel any advances that have not been completed.
   *
   * @param application
   * @returns {*}
   */
  cancelPendingAdvances(application) {
    const options = {};
    this.shData.addIncludesToOptions(options, []);

    return this.get('api').post(
      `applications/${application.get('id')}/cancel-pending-advances`,
      options
    );
  },

  /**
   * Pay and advance for a specific application
   *
   * @param {Application} application
   * @param {Number} baseAmount
   * @param {String} baseDescription
   * @param {Boolean} expedite
   * @param {Ember.A<ProjectLineItem>} advanceProjectLineItemIds
   * @return {Promise}
   */
  createAdvance({
    application,
    baseAmount,
    baseDescription,
    expedite,
    advanceProjectLineItemIds,
  }) {
    let options = {
      jsonData: {
        'base-amount' : baseAmount,
        'base-description' : baseDescription,
        'expedite' : expedite,
        'project-line-item-ids' : advanceProjectLineItemIds,
      }
    };

    this.shData.addIncludesToOptions(options, []);

    return this.get('api').post(`applications/${application.get('id')}/create-advance`, options);
  },

  /**
   * Reconcile billing associated with a particular application
   *
   * @param {Application} application
   * @return {Promise}
   */
  reconcileBilling(application) {
    return this.get('api').post(`applications/${application.get('id')}/reconcile-billing`);
  },

  /**
   * Remove an asset from an application
   *
   * @param {Application} application
   * @param {Asset} asset
   * @param {Array<String>=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  removeAsset(application, asset) {
    return this.api.post(`applications/${application.id}/assets/${asset.id}/detach`, {});
  },

  /**
   * Remove a favorite status from an application.
   *
   * @param {Application} application
   */
  removeFavorite(application) {
    application.set('isFavorite', false);
    return new Promise((resolve, reject) => {
      application.removeFavorite().then(() => {

        resolve(application);
      }).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error removing user from your network. Please refresh and try again.',
          'removeFavorite -- error removing favorite. app id: ' + application.get('id'),
        );

        application.set('isFavorite', true);
        reject(e);
      });
    });
  },

  /**
   * Remove followers for the given application id
   *
   * @param {String} applicationId
   * @param {Array<String>} followerIds
   * @param {Array<String>} opt_includes
   */
  removeFollowers(applicationId, followerIds, opt_includes = []) {
    let options = {
      jsonData: {
        'user-ids' : followerIds
      }
    };

    this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').post(`applications/${applicationId}/removeFollowers`, options).then(() => {
        let application = this.get('store').peekRecord('application', applicationId);
        if (!application) {
          resolve(true);
          return;
        }

        let followers = application.get('followers');
        followerIds.forEach(followerId => {
          let userFromCache = this.get('store').peekRecord('user', followerId);
          if (userFromCache) {
            followers.removeObject(userFromCache);
          }
        });

        resolve(true);
      }).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error saving your followers. Please refresh and try again.',
          'removeFollowers -- error removing followers. app id: ' + applicationId,
        );
        reject(e);
      });
    });
  },

  /**
   * Reopen the application
   *
   * @param {Applicaiton} application
   * @return {Ember.RSVP.Promise}
   */
  reopenApplication(application) {
    return new Promise((resolve, reject) => {
      let options = {};
      this.shData.addIncludesToOptions(options, []);

      this.get('api').post(`applications/${application.get('id')}/re-open/`, options).then(() => {
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error marking the application as active. Please refresh and try again.',
          'reOpenApplication -- error reopening app id: ' + application.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Given the text value search for the term.
   *
   * @param {String} searchTextValue
   * @param {String=} opt_assignmentId
   * @param {String=} opt_cacheKey
   * @param {Array=} additional_includes     Additional includes for the api.
   */
  searchApplications(searchTextValue, opt_assignmentId, opt_cacheKey, additional_includes = []) {
    let options = {
      params: {
        search: searchTextValue,
        cache_key: opt_cacheKey
      }
    };

    if (opt_assignmentId) {
      options.params['filter'] = {
        assignment_id: opt_assignmentId
      };
    }

    const includes = ['pitch.image,seller,thread.messages', additional_includes].join(',');
    options.params.include = includes;

    return new Promise((resolve, reject) => {
      this.get('store').query('application', options.params).then((applicationsResponse) => {
        resolve(applicationsResponse);
      }).catch((e) => {
        this.get('errorTracker').error('searchApplications failed - ' + e.toString(), this);
        reject(e);
      });
    });
  },

  /**
   * Given the search text make a database call to find application summaries for this value.
   *
   * @param {String} searchTextValue
   */
  searchDashboardList(searchTextValue) {
    let options = {
      params: {
        format: 'dashboard',
        search: searchTextValue
      }
    };

    return new Promise((resolve, reject) => {
      this.get('api').request('applications', options).then((response) => {
        let dashboardList = this._parseDashboardList(response);
        resolve(dashboardList);
      }).catch(e => {
        this.get('errorTracker').error('searchDashboardList failed - ' + e.toString(), this);
        reject(e);
      });
    });
  },

  /**
   * Set allows expenses on an application
   *
   * @param {Application} application
   * @param {boolean} enabled
   * @returns {Promise}
   */
  setExpensesEnabled(application, enabled) {
    const options = {
      jsonData: {
        enabled: enabled ? 1 : 0,
      },
    };

    return this.api.post(`applications/${application.id}/set-expenses-enabled`, options);
  },

  /**
   * Set allow buyer to cover seller fees on an application
   *
   * @param {Application} application
   * @param {boolean} enabled
   * @returns {Promise}
   */
  setCoverSellerFeesEnabled(application) {
    return this.api.post(`applications/${application.id}/set-seller-fees-covered`);
  },

  setExpediteCommitmentEnabled(application) {
    return this.api.post(`applications/${application.id}/set-expedite-commitment`)
  },

  /**
   * Set the Select status of an application
   *
   * @param {Application} application
   * @param {Boolean} select
   */
  setSelectStatus(application, select) {
    const options = {
      params: {
        select,
      }
    }

    return this.api.post(`applications/${application.id}/set-select`, options);
  },

  /**
   * Update the model from the server and handle any errors necessary.
   *
   * @param {Application} application
   * @param {Object} propsToUpdate
   * @param {Array<String>} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  updateApplication(application, propsToUpdate, opt_includes = []) {
    let options = {
      jsonData: propsToUpdate
    };

    options = this.shData.addIncludesToOptions(options, opt_includes);

    return new Promise((resolve, reject) => {
      this.get('api').put(`applications/${application.get('id')}/`, options).then(() => {
        resolve(application);
      }).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error updating your application. Please refresh and try again.',
          'updateApplication -- error updating app id: ' + application.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Updates the FeeStructure for a given Application
   *
   * @param {DS.Model} application
   * @param {Object} feeStructureJSON
   * @returns {Promise} API call to update the application FeeStructure
   */
  updateFeeStructure(application, feeStructureJSON) {
    const applicationID = application.get('id');
    const endpoint = `applications/${applicationID}/fee-structure`;

    const options = {
      jsonData: feeStructureJSON
    };

    return this.get('api').put(endpoint, options);
  },

  /**
   * @param {DS.Model|string} application
   * @param {string} title
   * @returns {Promise<void>}
   */
  updateApplicationTitle(application, title) {
    const applicationId = (application instanceof Model) ? application.get('id') : application;

    const options = this.shData.addIncludesToOptions({
      jsonData: { title },
    }, ['thread.name']);

    return this.get('api').put(`applications/${applicationId}/update-title`, options).then((response) => {
      const store = this.get('store');
      store.pushPayload(response);

      const newApplication = store.peekRecord('application', response.data.id);
      const { id, title } = newApplication.getProperties(['id', 'title']);

      // need to update any existing activitySummary models without setting dirty bit
      if (store.peekRecord('applicationSummary', id)) {
        store.push({ data: { id, type: 'application-summary', attributes: { title }}});
      }

      return newApplication;
    }).catch((e) => {
      this.get('errorTracker').error(`There was an error updating title for application [${applicationId}]`, this, e);
      throw e;
    });
  },

  /**
   * Update the model from the server and handle any errors necessary.
   *
   * @param {Application} application
   * @param {Object} googleLocation
   * @return {Ember.RSVP.Promise}
   */
  updateApplicationLocation(application, googleLocation) {
    const endpoint = `applications/${application.id}/location`;
    const options = {
      jsonData: {
        place_id: googleLocation.placeId,
      },
    };

    return this.api.post(endpoint, options);
  },

  /**
   * Delete the location from the server and handle any errors necessary.
   *
   * @param {Application} application
   * @return {Ember.RSVP.Promise}
   */
  deleteApplicationLocation(application) {
    const endpoint = `applications/${application.get('id')}/location/${application.get('location.id')}`;
    const options = {};

    return new Promise((resolve, reject) => {
      this.get('api').delete(endpoint, options).then(() => {
        resolve(application);
      }).catch((e) => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error updating your application. Please refresh and try again.',
          'updateApplication -- error updating app id: ' + application.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Withdraw a cancellation on the application
   *
   * @param {Application} application
   * @return {Ember.RSVP.Promise}
   */
  withdrawCancellation(application) {
    return new Promise((resolve, reject) => {
      let options = {};
      this.shData.addIncludesToOptions(options, []);

      this.get('api').post(`applications/${application.get('id')}/withdraw-cancel-request/`, options).then(() => {
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error withdrawing your cancellation request. Please refresh and try again.',
          'withdrawCancellation -- error withdrawing cancellation app id: ' + application.get('id'),
        );
        reject(e);
      });
    });
  },

  /**
   * Withdraw a completion request on an application
   *
   * @param {Application} application
   * @return {Ember.RSVP.Promise}
   */
  withdrawCompleteRequest(application, opt_feedback) {
    const options = {
      params: {},
    };

    if (opt_feedback) {
      options.params['feedback_reason'] = opt_feedback.reason;
      options.params['feedback_text'] = opt_feedback.otherText;
    }

    return this.api.post(`applications/${application.id}/withdraw-complete-request/`, options);
  },

  /**
   * Withdraw the expedited payout on an application
   *
   * @param {Application} application
   * @param {Array<String>=} opt_applicationIncludes
   * @return {Ember.RSVP.Promise}
   */
  withdrawExpeditedPayout(application, opt_applicationIncludes = []) {
    const options = this.shData.addIncludesToOptions({}, opt_applicationIncludes);

    return this.get('api').post(`applications/${application.get('id')}/unmark-expedited/`, options).then(response => {
      return this._pushApplicationResponseToStore(response);
    }).catch(e => {
      handleErrorResponseWithModal(
        this,
        e,
        'There was an error expediting your payout. Please refresh and try again.',
        'expeditePayout -- error expediting payout app id: ' + application.get('id'),
      );

      throw e;
    });
  },

  /**
   * Parse the response into the store and appropriate objects.
   *
   * @param {Object} response   The json object
   * @private
   */
  _parseDashboardList(response) {
    if (!response.data) {
      this.get('errorTracker').error('_parseDashboardList no data - ', this);
    }

    const store = this.store;
    const applicationDashboardList = store.createRecord('applicationDashboardList');

    this.shData.pushResponseToStore(response);

    applicationDashboardList.set('applicationSummaries', this.shData.getModelArrayFromResponse(response, 'applicationSummary'));

    //add meta info
    let meta = response.meta;
    if (meta) {
      applicationDashboardList.set('inProgressCount', meta.in_progress_count);
      applicationDashboardList.set('inProgressBuyerCount', meta.in_progress_buyer_count);
      applicationDashboardList.set('completedCount', meta.complete_count);
      applicationDashboardList.set('cancelledCount', meta.cancelled_count);
      applicationDashboardList.set('cancelledBuyerCount', meta.cancelled_buyer_count);
      applicationDashboardList.set('inProgressIsRead', meta.in_progress_is_read);
      applicationDashboardList.set('completedIsRead', meta.complete_is_read);
      applicationDashboardList.set('cancelledIsRead', meta.cancelled_is_read);
    } else {
      this.get('errorTracker').error('_parseDashboardList no meta - ', this);
    }

    //add pagination
    let paginationMeta = meta.pagination,
      pagination = store.createRecord('pagination');
    if (paginationMeta) {
      pagination.set('total', paginationMeta.total);
      pagination.set('count', paginationMeta.count);
      pagination.set('perPage', paginationMeta.per_page);
      pagination.set('currentPage', paginationMeta.current_page);
      pagination.set('totalPages', paginationMeta.total_pages);
    } else {
      this.get('errorTracker').error('_parseDashboardList no pagination - ', this);
    }

    applicationDashboardList.set('pagination', pagination);
    return applicationDashboardList;
  },

  /**
   * Push the application response to the store and return the application model.
   *
   * @param {Object} applicationResponse
   * @return {Application}
   * @private
   */
  _pushApplicationResponseToStore(applicationResponse) {
    const store = this.get('store');
    store.pushPayload(applicationResponse);
    return store.peekRecord('application', applicationResponse.data.id);
  },
});
